<template>
    <div class="page-container">
        <div class="settings-form">
            <h2 class="page-title">阿里云OSS配置</h2>
            <el-form :model="ossForm" label-width="140px" class="compact-form">
                <!-- AccessKey ID -->
                <el-form-item label="阿里云ID：" class="form-item-row">
                    <el-input v-model="ossForm.aliyun_keyid" placeholder="请输入阿里云AccessKey ID" size="large" show-password
                        clearable class="oss-input">
                        <template #prefix>
                            <el-icon>
                                <key />
                            </el-icon>
                        </template>
                    </el-input>
                </el-form-item>

                <!-- AccessKey Secret -->
                <el-form-item label="Secret：" class="form-item-row">
                    <el-input v-model="ossForm.aliyun_secret" placeholder="请输入阿里云AccessKey Secret" size="large"
                        show-password clearable class="oss-input">
                        <template #prefix>
                            <el-icon>
                                <lock />
                            </el-icon>
                        </template>
                    </el-input>
                </el-form-item>

                <!-- Endpoint -->
                <el-form-item label="存储地域：" class="form-item-row">
                    <el-input v-model="ossForm.endpoint" placeholder="例如：oss-cn-hangzhou.aliyuncs.com" size="large"
                        clearable class="oss-input">
                        <template #prefix>
                            <el-icon>
                                <location />
                            </el-icon>
                        </template>
                    </el-input>
                    <div class="oss-tip">
                        <el-icon class="tip-icon"><info-filled /></el-icon>
                        <span>请填写Bucket所在区域的外网Endpoint</span>
                    </div>
                </el-form-item>

                <!-- Bucket名称 -->
                <el-form-item label="Bucket：" class="form-item-row">
                    <el-input v-model="ossForm.aliyun_bucket" placeholder="请输入Bucket名称" size="large" clearable
                        class="oss-input">
                        <template #prefix>
                            <el-icon>
                                <box />
                            </el-icon>
                        </template>
                    </el-input>
                </el-form-item>

                <!-- 测试连接 -->
                <el-form-item label="连接测试：" class="form-item-row">
                    <el-button type="info" size="large" class="test-btn" @click="testConnection" :loading="testing">
                        <el-icon class="btn-icon">
                            <connection />
                        </el-icon>
                        测试OSS连接
                    </el-button>
                    <div class="test-result" v-if="testResult !== null">
                        <el-tag :type="testResult ? 'success' : 'danger'" size="large">
                            {{ testResult ? '连接成功' : '连接失败' }}
                        </el-tag>
                        <span class="result-message" v-if="testMessage">{{ testMessage }}</span>
                    </div>
                </el-form-item>

                <!-- 操作按钮 -->
                <el-form-item class="form-actions">
                    <el-button type="primary" size="large" class="submit-btn" @click="submitOssConfig"
                        :loading="loading">
                        <el-icon class="btn-icon">
                            <check />
                        </el-icon>
                        保存配置
                    </el-button>
                    <el-button type="info" size="large" class="reset-btn" @click="resetOssConfig">
                        <el-icon class="btn-icon">
                            <refresh />
                        </el-icon>
                        重置表单
                    </el-button>
                </el-form-item>
            </el-form>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Key,
    Lock,
    Location,
    Box,
    Connection,
    Check,
    Refresh,
    InfoFilled
} from '@element-plus/icons-vue'

export default {
    name: 'SystemOssConfig',
    components: { Key, Lock, Location, Box, Connection, Check, Refresh, InfoFilled },
    data() {
        return {
            loading: false,
            testing: false,
            testResult: null,
            testMessage: '',
            ossForm: {
                aliyun_keyid: '',
                aliyun_secret: '',
                endpoint: '',
                aliyun_bucket: ''
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchOssConfig()
    },
    methods: {
        async fetchOssConfig() {
            try {
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/LaBasicSettingsController.php`, {
                    action: 'get_oss',
                    store_id: this.userInfo.id
                })

                if (data.data) {
                    this.ossForm = {
                        aliyun_keyid: data.data.aliyun_keyid || '',
                        aliyun_secret: data.data.aliyun_secret || '',
                        endpoint: data.data.endpoint || '',
                        aliyun_bucket: data.data.aliyun_bucket || ''
                    }
                }
            } catch (error) {
                this.$message.error('获取OSS配置失败')
                console.error('获取OSS配置错误:', error)
            }
        },

        async testConnection() {
            if (!this.ossForm.aliyun_keyid || !this.ossForm.aliyun_secret ||
                !this.ossForm.endpoint || !this.ossForm.aliyun_bucket) {
                this.$message.warning('请填写完整的OSS配置信息')
                return
            }

            this.testing = true
            this.testResult = null
            this.testMessage = ''

            try {
                // 这里应该调用后端API进行测试连接
                // 示例代码，实际需要根据您的后端API调整
                const { data } = await axios.post(`${config.BASE_URL}/shop/api/test_oss_connection.php`, {
                    ...this.ossForm,
                    store_id: this.userInfo.id
                })

                this.testResult = data.success
                this.testMessage = data.message || (data.success ? '成功连接到OSS服务' : '连接OSS服务失败')

                if (data.success) {
                    this.$message.success('OSS连接测试成功')
                } else {
                    this.$message.error('OSS连接测试失败: ' + (data.message || '未知错误'))
                }
            } catch (error) {
                this.testResult = false
                this.testMessage = error.response?.data?.message || error.message || '测试连接时发生错误'
                this.$message.error('测试连接失败: ' + this.testMessage)
                console.error('OSS连接测试错误:', error)
            } finally {
                this.testing = false
            }
        },

        async submitOssConfig() {
            if (!this.ossForm.aliyun_keyid || !this.ossForm.aliyun_secret ||
                !this.ossForm.endpoint || !this.ossForm.aliyun_bucket) {
                this.$message.warning('请填写完整的OSS配置信息')
                return
            }

            this.loading = true
            try {
                const { data } = await axios.post(
                    `${config.BASE_URL}/shop/api/LaBasicSettingsController.php`,
                    {
                        action: 'update_oss',
                        store_id: this.userInfo.id,
                        ...this.ossForm
                    }
                )

                if (data.success) {
                    this.$message.success('OSS配置保存成功')
                    this.fetchOssConfig()
                } else {
                    this.$message.error(data.error || '保存失败')
                }
            } catch (error) {
                const errorMsg = error.response?.data?.error || error.message || '服务器错误'
                this.$message.error(`保存失败：${errorMsg}`)
                console.error('保存OSS配置错误:', error)
            } finally {
                this.loading = false
            }
        },

        resetOssConfig() {
    this.$confirm('请选择重置方式：', '重置配置', {
        confirmButtonText: '清空表单',
        cancelButtonText: '恢复默认',
        distinguishCancelAndClose: true,
        type: 'warning'
    }).then(() => {
        // 点击"清空表单"按钮
        this.clearForm();
    }).catch((action) => {
        // 点击"恢复默认"按钮或关闭弹窗
        if (action === 'cancel') {
            this.resetToDefault();
        }
    });
},

        // 清空表单
        clearForm() {
            this.ossForm = {
                aliyun_keyid: '',
                aliyun_secret: '',
                endpoint: '',
                aliyun_bucket: ''
            };
            this.testResult = null;
            this.testMessage = '';
            this.$message.success('表单已清空');
        },

        // 重置为默认配置
        async resetToDefault() {
    try {
        const { data } = await axios.post(
            `${config.BASE_URL}/shop/api/LaBasicSettingsController.php`,
            {
                action: 'clear_oss',
                store_id: this.userInfo.id
            }
        );

        if (data.success) {
            this.$message.success('配置已重置为默认值');
            this.fetchOssConfig(); // 重新加载
        } else {
            this.$message.error(data.error || '重置失败');
        }
    } catch (error) {
        this.$message.error('重置失败: ' + (error.response?.data?.error || error.message));
        console.error('重置OSS配置错误:', error);
    }
}
    }
}
</script>

<style scoped lang="scss">
.page-container {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.compact-form {
    :deep(.el-form-item__label) {
        font-size: 16px;
        color: #374151;
        line-height: 40px;
        padding-right: 24px !important;
    }

    .form-item-row {
        margin-bottom: 28px;

        :deep(.el-form-item__content) {
            flex: 1;
            min-width: 0;
        }
    }
}

.oss-input {
    width: 100%;
    max-width: 500px;
}

.oss-tip {
    margin-top: 8px;
    margin-left: 10px;
    color: #64748b;
    font-size: 13px;
    line-height: 1.5;
    display: flex;
    align-items: center;

    .tip-icon {
        color: #3b82f6;
        margin-right: 8px;
        font-size: 16px;
    }
}

.test-btn {
    width: 200px;
    padding: 14px 24px;

    .btn-icon {
        margin-right: 8px;
        font-size: 16px;
    }
}

.test-result {
    margin-top: 12px;
    display: flex;
    align-items: center;
    gap: 12px;

    .result-message {
        color: #64748b;
        font-size: 13px;
    }
}

.form-actions {
    margin-top: 40px;
    text-align: center;

    .submit-btn,
    .reset-btn {
        width: 160px;
        padding: 14px 24px;
        font-size: 15px;
        letter-spacing: 0.5px;

        .btn-icon {
            margin-right: 8px;
            font-size: 16px;
        }
    }
}

// 调整弹窗样式
.reset-confirm {
    .el-message-box__content {
        padding: 20px;
        text-align: center;
    }

    .el-message-box__btns {
        display: flex;
        justify-content: space-around;
    }
}
</style>